import React from "react";
import NoData from "../../components/noData/noData";
import {
  HiOutlinePhone,
  HiOutlineMail,
  HiOutlineOfficeBuilding,
  HiOutlineIdentification,
  HiOutlineCalendar,
  HiOutlineLocationMarker,
  HiOutlineUser,
  HiOutlineHeart,
  HiOutlineAcademicCap,
} from "react-icons/hi";
import { FaRegUser } from "react-icons/fa";

// ── Skeleton ──────────────────────────────────────────────────────────────────
const Skeleton = () => (
  <div className="animate-pulse space-y-4">
    <div className="h-48 bg-gray-200 rounded-2xl" />
    <div className="grid grid-cols-2 gap-4">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="h-16 bg-gray-100 rounded-xl" />
      ))}
    </div>
  </div>
);

// ── Field item inside a card ──────────────────────────────────────────────────
const Field = ({ label, value }) => (
  <div className="flex flex-col gap-0.5">
    <span className="text-[10.5px] font-semibold text-gray-400 uppercase tracking-wide">
      {label}
    </span>
    <span className="text-[13.5px] font-semibold text-blue-900 break-words leading-snug">
      {value || <span className="text-gray-300 font-normal">—</span>}
    </span>
  </div>
);

// ── Section card ──────────────────────────────────────────────────────────────
const SectionCard = ({ icon, title, children, cols = 2 }) => (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
    <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-gray-100">
      <span className="w-7 h-7 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center text-[15px]">
        {icon}
      </span>
      <h3 className="text-[13px] font-bold text-blue-900 tracking-tight">{title}</h3>
    </div>
    <div className={`grid grid-cols-${cols} gap-x-8 gap-y-5 p-5`}>
      {children}
    </div>
  </div>
);

// ── Avatar ────────────────────────────────────────────────────────────────────
const Avatar = ({ image, name, size = "lg" }) => {
  const dim = size === "lg" ? "w-20 h-20 text-2xl" : "w-14 h-14 text-lg";
  const initials = name
    ? name
        .split(" ")
        .slice(0, 2)
        .map((w) => w[0])
        .join("")
        .toUpperCase()
    : "?";

  return (
    <div
      className={`${dim} rounded-2xl ring-4 ring-white/30 overflow-hidden bg-white/20 flex items-center justify-center flex-shrink-0`}
    >
      {image ? (
        <img src={image} alt={name} className="w-full h-full object-cover" />
      ) : (
        <span className="font-bold text-white">{initials}</span>
      )}
    </div>
  );
};

// ── Stat chip (used in hero) ──────────────────────────────────────────────────
const HeroChip = ({ icon, label, value }) => {
  if (!value) return null;
  return (
    <div className="flex items-center gap-1.5 bg-white/15 backdrop-blur-sm px-3 py-1.5 rounded-full">
      <span className="text-white/70 text-[13px]">{icon}</span>
      <span className="text-[11px] text-white/60 font-medium">{label}:</span>
      <span className="text-[11px] text-white font-semibold truncate max-w-[120px]">
        {value}
      </span>
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const OverviewProfile = ({ staff, user, loading }) => {
  if (loading) return <Skeleton />;

  if (!staff)
    return (
      <div className="bg-white rounded-2xl p-8">
        <NoData
          title="No Result Found"
          message="Please try again with another keywords or maybe use generic term"
          buttonText={null}
        />
      </div>
    );

  const displayName = staff.name || user?.name || "—";
  const designation = staff.designation?.name || user?.designation?.name;
  const department = staff.department?.name || user?.department?.name;

  return (
    <div className="flex flex-col gap-4">
      {/* ── Hero Banner ──────────────────────────────────────────────────── */}
      <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-blue-700 via-blue-600 to-indigo-700 shadow-lg">
        {/* Decorative circles */}
        <div className="absolute -top-8 -right-8 w-48 h-48 rounded-full bg-white/5 pointer-events-none" />
        <div className="absolute -bottom-12 -left-6 w-40 h-40 rounded-full bg-white/5 pointer-events-none" />

        <div className="relative z-10 p-6 flex flex-col sm:flex-row items-start sm:items-center gap-5">
          <Avatar image={staff.profile_image} name={displayName} size="lg" />

          <div className="flex-1 min-w-0">
            <h1 className="text-white font-bold text-xl capitalize leading-tight truncate">
              {displayName}
            </h1>
            <div className="flex flex-wrap items-center gap-2 mt-1.5">
              {designation && (
                <span className="bg-white/20 text-white text-[11px] font-semibold px-2.5 py-0.5 rounded-full">
                  {designation}
                </span>
              )}
              {department && (
                <span className="bg-white/10 text-white/80 text-[11px] font-medium px-2.5 py-0.5 rounded-full border border-white/20">
                  {department}
                </span>
              )}
              {staff.emp_type && (
                <span className="bg-indigo-500/40 text-white/90 text-[11px] font-medium px-2.5 py-0.5 rounded-full">
                  {staff.emp_type}
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              <HeroChip icon={<HiOutlinePhone />} label="Phone" value={staff.phone} />
              <HeroChip icon={<HiOutlineMail />} label="Email" value={staff.email} />
              <HeroChip
                icon={<HiOutlineCalendar />}
                label="Joined"
                value={staff.joining_at}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ── Cards grid ───────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Personal Info */}
        <SectionCard icon={<HiOutlineUser />} title="Personal Information">
          <Field label="Date of Birth" value={staff.dob} />
          <Field label="Gender" value={staff.gender} />
          <Field label="Marital Status" value={staff.marital_status} />
          <Field label="Date of Marriage" value={staff.date_of_marriage} />
          <Field label="Partner Name" value={staff.spouse_name} />
        </SectionCard>

        {/* Family Info */}
        <SectionCard icon={<HiOutlineHeart />} title="Family Information">
          <Field label="Father Name" value={staff.father_name} />
          <Field label="Mother Name" value={staff.mother_name} />
        </SectionCard>

        {/* Contact Info */}
        <SectionCard icon={<HiOutlinePhone />} title="Contact Information">
          <Field label="Phone Number" value={staff.phone} />
          <Field label="Alternate Phone" value={staff.phone2} />
          <Field label="Email Address" value={staff.email} />
        </SectionCard>

        {/* Work Info */}
        <SectionCard icon={<HiOutlineIdentification />} title="Work Information">
          <Field label="Department" value={staff.department?.name} />
          <Field label="Designation" value={staff.designation?.name} />
          <Field label="Date of Joining" value={staff.joining_at} />
          <Field label="Employee Type" value={staff.emp_type} />
        </SectionCard>
      </div>

      {/* ── Address cards ─────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SectionCard icon={<HiOutlineLocationMarker />} title="Current Address">
          <Field label="Country" value={staff.country} />
          <Field label="State" value={staff.state} />
          <Field label="City" value={staff.city} />
          <Field label="Pincode" value={staff.pincode} />
          <Field label="Full Address" value={staff.address} />
        </SectionCard>

        <SectionCard icon={<HiOutlineLocationMarker />} title="Permanent Address">
          <Field label="Country" value={staff.country2} />
          <Field label="State" value={staff.state2} />
          <Field label="City" value={staff.city2} />
          <Field label="Pincode" value={staff.pincode2} />
          <Field label="Full Address" value={staff.address2} />
        </SectionCard>
      </div>
    </div>
  );
};

export default OverviewProfile;
